import React from 'react';
import { Card, CardContent, CardActions, CardHeader, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';

/**
 * Styled Waltero Card with brand-specific styling
 */
const StyledWalteroCard = styled(Card)(({ theme, variant, interactive }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: '12px',
  border: `1px solid ${theme.palette.waltero.primary.neutralBlue}`,
  boxShadow: '0px 2px 12px rgba(2, 56, 97, 0.08)',
  transition: 'all 0.2s ease-in-out',
  
  // Variant styles
  ...(variant === 'elevated' && {
    boxShadow: '0px 4px 20px rgba(2, 56, 97, 0.12)',
    borderColor: theme.palette.waltero.primary.paleBlue
  }),
  
  ...(variant === 'outlined' && {
    border: `2px solid ${theme.palette.waltero.primary.lightBlue}`,
    boxShadow: 'none'
  }),
  
  ...(variant === 'accent' && {
    backgroundColor: theme.palette.waltero.secondary.backgroundApricot,
    borderColor: theme.palette.waltero.secondary.paleApricot
  }),
  
  // Interactive hover effects
  ...(interactive && {
    cursor: 'pointer',
    '&:hover': {
      boxShadow: '0px 6px 24px rgba(2, 56, 97, 0.15)',
      transform: 'translateY(-2px)',
      borderColor: theme.palette.waltero.primary.lightBlue
    },
    '&:active': {
      transform: 'translateY(-1px)'
    }
  })
}));

/**
 * WalteroCard - Branded container component
 * 
 * A styled card component that follows Waltero brand guidelines with
 * proper shadows, borders, and interactive states.
 * 
 * @param {Object} props - Component properties
 * @param {React.ReactNode} props.children - Card content
 * @param {string} [props.variant='default'] - Card style variant ('default', 'elevated', 'outlined', 'accent')
 * @param {boolean} [props.interactive=false] - Whether card should have hover/click effects
 * @param {string} [props.title] - Optional card title
 * @param {string} [props.subtitle] - Optional card subtitle
 * @param {React.ReactNode} [props.avatar] - Optional avatar for card header
 * @param {React.ReactNode} [props.action] - Optional action element for card header
 * @param {React.ReactNode} [props.actions] - Optional card actions footer
 * @param {Function} [props.onClick] - Optional click handler (enables interactive behavior)
 * @param {string} [props.className] - Additional CSS classes
 * @param {Object} [props.sx] - Additional MUI sx props
 */
const WalteroCard = ({
  children,
  variant = 'default',
  interactive = false,
  title,
  subtitle,
  avatar,
  action,
  actions,
  onClick,
  className,
  sx,
  ...otherProps
}) => {
  const theme = useTheme();
  
  // Enable interactive behavior if onClick is provided
  const isInteractive = interactive || !!onClick;
  
  const handleClick = (event) => {
    if (onClick) {
      onClick(event);
    }
  };
  
  // Filter out custom props to avoid DOM warnings
  const { interactive: _, ...filteredProps } = otherProps;
  
  return (
    <StyledWalteroCard
      variant={variant}
      interactive={isInteractive}
      onClick={handleClick}
      className={className}
      sx={sx}
      {...filteredProps}
    >
      {(title || subtitle || avatar || action) && (
        <CardHeader
          avatar={avatar}
          action={action}
          title={title}
          subheader={subtitle}
          titleTypographyProps={{
            variant: 'h6',
            color: theme.palette.waltero.primary.coreBlue,
            fontWeight: 600
          }}
          subheaderTypographyProps={{
            variant: 'body2',
            color: theme.palette.waltero.primary.mediumBlue
          }}
        />
      )}
      
      <CardContent>
        {children}
      </CardContent>
      
      {actions && (
        <CardActions
          sx={{
            padding: theme.spacing(2),
            paddingTop: 0,
            justifyContent: 'flex-end'
          }}
        >
          {actions}
        </CardActions>
      )}
    </StyledWalteroCard>
  );
};

export default WalteroCard;